import { useContext } from "react";
import { AppStateContext } from "../../../state/AppProvider";
import { assistantsList, AuthRequiredError } from "../../../api/api";
import { Assistant } from "../../../api/models";

interface UseAssistantsRefreshOptions {
    /**
     * When true, also dispatch UPDATE_ASSISTANT with the active gpt4 assistant
     * after a successful fetch (AssistantEditor / AssistantManager behaviour).
     */
    updateActiveAssistant?: boolean;
    /**
     * When true, also clear the custom + shared assistant lists on error
     * (AssistantCreator behaviour). When false, only FETCH_ASSISTANTS is
     * reset to null (AssistantEditor / AssistantManager behaviour).
     */
    clearListsOnError?: boolean;
}

/**
 * Shared, de-duplicated `refreshAssistantsList` used by the Creator, Editor and
 * Manager. Behaviour is parameterised so each call site keeps its exact original
 * dispatch sequence. Returns the fetched assistants list (or null on failure),
 * mirroring the Creator's original return value.
 */
export const useAssistantsRefresh = ({
    updateActiveAssistant = false,
    clearListsOnError = false
}: UseAssistantsRefreshOptions = {}) => {
    const appStateContext = useContext(AppStateContext);

    return async (): Promise<Assistant[] | null> => {
        try {
            const response = await assistantsList();
            if (response) {
                appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: response });
                appStateContext?.dispatch({
                    type: "UPDATE_CUSTOM_ASSISTANTS",
                    payload: response.filter(
                        (assist) => assist.type === "custom" && assist.can_edit
                    )
                });
                appStateContext?.dispatch({
                    type: "UPDATE_SHARED_ASSISTANTS",
                    payload: response.filter(
                        (assist) => assist.type === "custom" && !assist.can_edit
                    )
                });
                if (updateActiveAssistant) {
                    appStateContext?.dispatch({
                        type: "UPDATE_ASSISTANT",
                        payload: response.filter((assistant) => assistant.assistant === "gpt4")[0]
                    });
                }
            } else {
                appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: null });
            }
            return response ?? null;
        } catch (err) {
            // The session-expired modal handles re-auth; don't wipe the lists for that.
            if (err instanceof AuthRequiredError) {
                return null;
            }
            appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: null });
            if (clearListsOnError) {
                appStateContext?.dispatch({ type: "UPDATE_CUSTOM_ASSISTANTS", payload: null });
                appStateContext?.dispatch({ type: "UPDATE_SHARED_ASSISTANTS", payload: null });
            }
            console.error("There was an issue fetching your assistants.", err);
            return null;
        }
    };
};
